import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, LogOut, Coins, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "./LanguageSelector";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, locale } = useLocale();
  const { user, profile, signOut } = useAuth();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { name: t("nav.events"), href: "#events" },
    { name: t("nav.howItWorks"), href: "#how-it-works" },
    { name: t("nav.leaderboard"), href: "/leaderboard", isRoute: true },
    { name: locale.language === "pt" ? "Loja" : "Store", href: "/store", isRoute: true },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleNavClick = (href: string) => {
    // If it's a hash link and we're not on the home page, navigate to home first
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/" + href);
      } else {
        // We're on home page, just scroll to the section
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium"
                >
                  {link.name}
                </Link>
              ) : (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link.href)}
                  className="text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium"
                >
                  {link.name}
                </button>
              )
            )}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSelector />
            
            {user ? (
              <>
                {profile && (
                  <button
                    onClick={() => navigate("/store")}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border/50 hover:bg-muted hover:border-primary/30 transition-all duration-200"
                  >
                    <Coins size={16} className="text-primary" />
                    <span className="text-sm font-medium">
                      {profile.duv_coins.toLocaleString()}
                    </span>
                  </button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/profile")}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border/50 hover:bg-muted"
                >
                  <User size={16} className="text-primary" />
                  <span className="text-sm font-medium">
                    {profile?.username || user.email?.split("@")[0]}
                  </span>
                </Button>
                {isAdmin && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate("/admin")}
                    className="flex items-center gap-2 text-primary"
                  >
                    <Shield size={16} />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={handleSignOut}>
                  <LogOut size={16} />
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate("/auth")}>
                {t("nav.login")}
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-foreground p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navLinks.map((link) =>
                link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors py-2 font-medium"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <button
                    key={link.name}
                    onClick={() => { handleNavClick(link.href); setIsOpen(false); }}
                    className="text-muted-foreground hover:text-foreground transition-colors py-2 font-medium text-left"
                  >
                    {link.name}
                  </button>
                )
              )}
              <div className="flex flex-col gap-2 pt-4 border-t border-border/50">
                <div className="flex justify-center py-2">
                  <LanguageSelector />
                </div>
                
                {user ? (
                  <>
                    {profile && (
                      <button
                        onClick={() => { navigate("/store"); setIsOpen(false); }}
                        className="flex items-center justify-center gap-2 py-2 hover:text-primary transition-colors"
                      >
                        <Coins size={16} className="text-primary" />
                        <span className="font-medium">
                          {profile.duv_coins.toLocaleString()} CallyCoins
                        </span>
                      </button>
                    )}
                    <Button variant="ghost" className="w-full" onClick={() => { navigate("/profile"); setIsOpen(false); }}>
                      <User size={16} className="mr-2" />
                      {locale.language === "pt" ? "Meu Perfil" : "My Profile"}
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" className="w-full text-primary" onClick={() => { navigate("/admin"); setIsOpen(false); }}>
                        <Shield size={16} className="mr-2" />
                        Admin Panel
                      </Button>
                    )}
                    <Button variant="ghost" className="w-full" onClick={handleSignOut}>
                      <LogOut size={16} className="mr-2" />
                      {locale.language === "pt" ? "Sair" : "Log Out"}
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" className="w-full" onClick={() => { navigate("/auth"); setIsOpen(false); }}>
                    {t("nav.login")}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
